/**
 * Phone number utility functions for Hong Kong numbers
 * Provides consistent formatting and validation across the application
 */

/**
 * Normalize phone number by removing spaces and unwanted characters
 * Keeps only digits and the + symbol
 * @param phone - Phone number to normalize
 * @returns Normalized phone number
 */
export const normalizePhoneNumber = (phone: string): string => {
  if (!phone) return '';
  // Remove all whitespace and keep only digits and +
  return phone.replace(/\s+/g, '').replace(/[^\d+]/g, '');
};

/**
 * Validate Hong Kong phone number format
 * Accepts: +852XXXXXXXX (8 digits after +852)
 * @param phone - Phone number to validate
 * @returns Whether the phone number is valid
 */
export const validateHKPhoneNumber = (phone: string): boolean => {
  if (!phone) return false;
  const normalized = normalizePhoneNumber(phone);
  // Accept either:
  // 1. +852XXXXXXXX (full format with country code)
  // 2. XXXXXXXX (just 8 digits)
  return /^\+852\d{8}$/.test(normalized) || /^\d{8}$/.test(normalized);
};

/**
 * Format phone number for database storage (no spaces)
 * @param phone - Phone number to format
 * @returns Phone number formatted for storage
 */
export const formatPhoneForStorage = (phone: string): string => {
  if (!phone) return '';
  return normalizePhoneNumber(phone);
};

/**
 * Format phone number for display (with space after country code)
 * @param phone - Phone number to format
 * @returns Phone number formatted for display
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return '';
  const normalized = normalizePhoneNumber(phone);

  // If it's a Hong Kong number, add space after +852
  if (/^\+852\d{8}$/.test(normalized)) {
    return normalized.replace(/^\+852/, '+852 ');
  }

  return normalized;
};

/**
 * Format phone with Hong Kong country code if not already present
 * Removes all non-digits, then adds 852 prefix
 * @param phone - Phone number to format
 * @returns Phone number with 852 country code
 */
export const formatPhoneWithCountryCode = (phone: string): string => {
  if (!phone) return '';

  const cleanPhone = phone.replace(/\D/g, ''); // Remove all non-digits

  // If already starts with 852, return as is
  if (cleanPhone.startsWith('852')) {
    return cleanPhone;
  }

  // If starts with 8 (partial 852), add 52
  if (cleanPhone.startsWith('8')) {
    return `852${cleanPhone}`;
  }

  // Otherwise, add full 852 prefix
  return `852${cleanPhone}`;
};

/**
 * Display phone number with fallback for null/empty values
 * @param phone - Phone number to display
 * @param fallback - Text to show if phone is null/empty
 * @returns Phone number formatted for display or fallback text
 */
export const displayPhoneNumber = (phone: string, fallback: string = 'No phone'): string => {
  if (!phone) return fallback;
  return formatPhoneForDisplay(phone);
};

/**
 * Get phone number validation error message for Hong Kong numbers
 * @param phone - Phone number to validate
 * @returns Error message if invalid, null if valid
 */
export const getPhoneNumberError = (phone: string): string | null => {
  if (!phone) return null; // Phone is optional in most cases

  if (!validateHKPhoneNumber(phone)) {
    return 'Please enter a valid Hong Kong phone number (+852XXXXXXXX)';
  }

  return null;
};
